import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Phone, MessageSquare, Check, X, Loader2, Plane, MapPin, Clock, Users, AlertTriangle, ShieldAlert, CheckCircle, Copy, ClipboardCheck, RefreshCw, Send, Car, IdCard, User, Ban, CalendarClock, History, BellRing, FileText, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ADMIN_BOOKINGS_URL } from "@/constants/adminRoutes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  PENDING: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  PENDING_REVIEW: "bg-red-500/15 text-red-400 border-red-500/30",
  CONFIRMED: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  IN_PROGRESS: "bg-green-500/15 text-green-400 border-green-500/30",
  COMPLETED: "bg-gray-500/15 text-gray-400 border-gray-500/30",
  DECLINED: "bg-red-900/15 text-red-300 border-red-900/30",
  CANCELLED: "bg-slate-500/15 text-slate-300 border-slate-500/30",
  PAID: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
};

function buildWhatsAppConfirmUrl(b) {
  const fare = b.fare_data;
  const total = fare?.total ? `₹${fare.total.toLocaleString("en-IN")}` : fare?.displayTotal || "TBD";
  const msg = `✅ Hi ${b.passenger_name}, your Sarathi-X ride is confirmed!\n\n📍 From: ${b.pickup_location}\n✈️ To: ${b.drop_location}\n📅 ${b.date} · ${b.time}\n🚗 MG Windsor EV\n💰 Fare: ${total} (fixed fare · no surge)\n🔖 Ref: ${b.booking_reference}\n\nYour driver's details will be shared 2 hours before pickup. Safe travels! 🙏`;
  const phone = (b.passenger_phone || "").replace(/[^0-9]/g, "");
  if (!phone) return "";
  return `https://wa.me/${phone.startsWith("91") ? phone : "91" + phone}?text=${encodeURIComponent(msg)}`;
}

function buildWhatsAppDeclineUrl(b, reason) {
  const msg = `Hi ${b.passenger_name}, unfortunately we are unable to confirm your Sarathi-X booking (Ref: ${b.booking_reference}) for ${b.date} at ${b.time}.\n\n${reason ? `Reason: ${reason}\n\n` : ""}Please WhatsApp us to check alternative slots. We apologise for the inconvenience.`;
  const phone = (b.passenger_phone || "").replace(/[^0-9]/g, "");
  if (!phone) return "";
  return `https://wa.me/${phone.startsWith("91") ? phone : "91" + phone}?text=${encodeURIComponent(msg)}`;
}

export default function AdminBookingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [clashData, setClashData] = useState(null);
  const [clashLoading, setClashLoading] = useState(false);
  const [copiedBtn, setCopiedBtn] = useState("");

  const clashStyle = (status) => {
    if (status === "HARD_CLASH") return { bg: "bg-red-500/10 border-red-500/30", text: "text-red-400", label: "Schedule Conflict Detected" };
    if (status === "WARNING") return { bg: "bg-yellow-500/10 border-yellow-500/30", text: "text-yellow-400", label: "Schedule Warning" };
    return { bg: "bg-green-500/10 border-green-500/30", text: "text-green-400", label: "Schedule Clear \u2014 No Conflicts" };
  };

  const buildConfirmationMsg = (b) => {
    const name = b.passenger_name;
    const ref = b.booking_reference;
    const pickup = b.pickup_location;
    const drop = b.drop_location;
    const date = b.date;
    const time = b.time;
    const pax = b.passengers || 1;
    const fare = b.fare_data?.displayTotal || b.fare_data?.totalFare || "TBD";
    const fareStr = typeof fare === "number" ? `\u20b9${fare.toLocaleString("en-IN")}` : fare;
    const flight = b.flight_number || "Not provided";
    const isAirport = b.ride_type === "airport";
    const isPickupAirport = isAirport && (pickup.includes("KIAL") || pickup.includes("Kempegowda") || pickup.includes("Terminal"));

    if (isAirport && isPickupAirport) {
      return `Hi ${name} \u{1F44B}\n\nYour Sarathi-X ride is confirmed!\n\n\u{1F4CB} Booking ref: ${ref}\n\u2708\uFE0F Pickup: ${pickup}\n\u{1F4CD} Drop: ${drop}\n\u{1F4C5} ${date}\n\u{1F554} Landing time: ${time}\n\u2708\uFE0F Flight: ${flight}\n\u{1F465} ${pax} Passenger(s)\n\n\u{1F4B0} Fixed Fare: ${fareStr}\n   Toll & GST included \u00B7 No surge\n\n\u{1F4B3} Payment: Pay now or after your ride\n   UPI \u00B7 Cash accepted\n\nWe will track your flight and adjust\nif your flight is delayed.\nYour driver details will be shared\n2 hours before your landing time.\n\nNeed to change or cancel?\nReply to this message.\n\n\u2014 Sarathi-X`;
    } else if (isAirport) {
      return `Hi ${name} \u{1F44B}\n\nYour Sarathi-X ride is confirmed!\n\n\u{1F4CB} Booking ref: ${ref}\n\u{1F4CD} Pickup: ${pickup}\n\u2708\uFE0F Drop: ${drop}\n\u{1F4C5} ${date}\n\u{1F554} ${time}\n\u{1F465} ${pax} Passenger(s)\n\n\u{1F4B0} Fixed Fare: ${fareStr}\n   Toll & GST included \u00B7 No surge\n\n\u{1F4B3} Payment: Pay now or after your ride\n   UPI \u00B7 Cash accepted\n\nYour driver details will be shared\n2 hours before pickup.\n\nNeed to change or cancel?\nReply to this message.\n\n\u2014 Sarathi-X`;
    } else {
      return `Hi ${name} \u{1F44B}\n\nYour Sarathi-X ride is confirmed!\n\n\u{1F4CB} Booking ref: ${ref}\n\u{1F4CD} Pickup: ${pickup}\n\u{1F3D9}\uFE0F Drop: ${drop}\n\u{1F4C5} ${date}\n\u{1F554} ${time}\n\u{1F465} ${pax} Passenger(s)\n\n\u{1F4B0} Fixed Fare: ${fareStr}\n   GST included \u00B7 No surge\n\n\u{1F4B3} Payment: Pay now or after your ride\n   UPI \u00B7 Cash accepted\n\nYour driver details will be shared\n2 hours before pickup.\n\nNeed to change or cancel?\nReply to this message.\n\n\u2014 Sarathi-X`;
    }
  };

  const buildDeclineMsg = (b) => {
    return `Hi ${b.passenger_name},\n\nThank you for choosing Sarathi-X.\n\nUnfortunately we are unable to confirm your booking request for ${b.date} at ${b.time} as the vehicle is unavailable for that slot.\n\nBooking ref: ${b.booking_reference}\n\nWe apologise for the inconvenience.\nIf you would like to book for a different time, please reply and we will do our best to accommodate.\n\n\u2014 Sarathi-X`;
  };

  const handleCopyMsg = (type) => {
    const text = type === "confirm" ? buildConfirmationMsg(booking) : buildDeclineMsg(booking);
    navigator.clipboard.writeText(text).then(() => {
      setCopiedBtn(type);
      setTimeout(() => setCopiedBtn(""), 2000);
    });
  };

  const refreshBooking = () => {
    setLoading(true);
    axios.get(`${API}/admin/bookings/${id}`)
      .then(res => {
        setBooking(res.data);
      })
      .catch(() => toast.error("Booking not found"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/bookings/${id}`)
      .then(res => {
        setBooking(res.data);
        // Fetch clash detection after booking loads
        setClashLoading(true);
        axios.get(`${API}/admin/bookings/${id}/clash-check`)
          .then(cr => setClashData(cr.data))
          .catch(() => setClashData(null))
          .finally(() => setClashLoading(false));
      })
      .catch(() => toast.error("Booking not found"))
      .finally(() => setLoading(false));
  }, [id]);

  const handleConfirm = async () => {
    setSaving("confirm");
    try {
      const res = await axios.put(`${API}/admin/bookings/${id}/confirm`, {});
      setBooking(res.data);
      const wa = res.data._whatsapp_send;
      if (wa?.success && !wa?.skipped) {
        toast.success("Confirmed · WhatsApp sent to customer");
      } else if (wa?.skipped) {
        toast.success("Confirmed (WhatsApp already sent earlier)");
      } else if (wa?.error_code === "CONFIG") {
        toast.success("Booking confirmed!");
        window.open(buildWhatsAppConfirmUrl(res.data), "_blank");
      } else {
        toast.error("Confirmed, but WhatsApp send failed. Use Copy button.");
      }
    } catch (e) {
      if (e?.response?.status !== 401) {
        toast.error(e?.response?.data?.detail || "Failed to confirm");
      }
    }
    finally { setSaving(""); }
  };

  const handleDecline = async () => {
    setSaving("decline");
    try {
      const res = await axios.put(`${API}/admin/bookings/${id}/decline`, { reason: "" });
      setBooking(res.data);
      const wa = res.data._whatsapp_send;
      if (wa?.success && !wa?.skipped) {
        toast.success("Declined · WhatsApp sent to customer");
      } else if (wa?.skipped) {
        toast.success("Declined (WhatsApp already sent earlier)");
      } else if (wa?.error_code === "CONFIG") {
        toast.success("Booking declined");
        window.open(buildWhatsAppDeclineUrl(res.data, ""), "_blank");
      } else {
        toast.error("Declined, but WhatsApp send failed. Use Copy button.");
      }
    } catch (e) {
      if (e?.response?.status !== 401) {
        toast.error(e?.response?.data?.detail || "Failed to decline");
      }
    }
    finally { setSaving(""); }
  };

    const handleStatusChange = async (status) => {
    setSaving("status");
    try {
      const res = await axios.put(`${API}/admin/bookings/${id}/status`, { status });
      setBooking(res.data);
      toast.success(`Status updated to ${status}`);
    } catch (e) {
      // 401 → handled globally by the AuthContext interceptor (session redirect
      // + "Session expired" toast). For everything else surface the backend
      // detail so the operator isn't stuck on a generic "Failed to update".
      if (e?.response?.status !== 401) {
        toast.error(e?.response?.data?.detail || "Failed to update status");
      }
    }
    finally { setSaving(""); }
  };

  if (loading) return <div className="text-sarathi-text-muted text-center py-20">Loading...</div>;
  if (!booking) return <div className="text-red-400 text-center py-20">Booking not found</div>;

  const fare = booking.fare_data || {};
  const needsReview = booking.status === "PENDING_REVIEW" || fare?.adminFlags?.requiresManualReview;
  const isPending = booking.status === "PENDING" || booking.status === "PENDING_REVIEW";

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="booking-detail">
      {/* Back + Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(ADMIN_BOOKINGS_URL)} className="text-sarathi-text-muted hover:text-white transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="font-outfit text-lg font-bold text-white">{booking.booking_reference}</h1>
            <span className={`text-xs px-2.5 py-0.5 rounded-full border ${STATUS_COLORS[booking.status] || STATUS_COLORS.PENDING}`}>{booking.status}</span>
            {needsReview && <AlertTriangle className="w-4 h-4 text-red-400" />}
          </div>
          <p className="text-sarathi-text-muted text-xs">Created {new Date(booking.created_at).toLocaleString("en-IN")}</p>
        </div>
      </div>

      {/* Needs Review Banner */}
      {needsReview && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-400 shrink-0" />
          <div>
            <p className="text-red-400 text-sm font-medium">Manual Review Required</p>
            <p className="text-red-300/70 text-xs">{fare?.classificationReason || fare?.adminFlags?.reviewReason || "Edge-case classification"}</p>
          </div>
        </div>
      )}

      {/* Cancellation Context — visible when customer (or admin) cancelled the booking */}
      {booking.status === "CANCELLED" && (
        <div className="bg-slate-500/10 border border-slate-500/30 rounded-xl p-4" data-testid="cancellation-context">
          <div className="flex items-center gap-2 mb-2">
            <Ban className="w-4 h-4 text-slate-300" />
            <p className="text-slate-200 text-sm font-semibold">Cancelled by {booking.cancelled_by || "customer"}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div>
              <p className="text-sarathi-text-muted">Reason</p>
              <p className="text-white">{booking.cancellation_reason || "—"}</p>
            </div>
            <div>
              <p className="text-sarathi-text-muted">Cancelled at</p>
              <p className="text-white">{booking.cancelled_at ? new Date(booking.cancelled_at).toLocaleString("en-IN") : "—"}</p>
            </div>
            <div>
              <p className="text-sarathi-text-muted">Hours before pickup</p>
              <p className="text-white">
                {booking.hours_before_pickup_at_cancel != null
                  ? `${booking.hours_before_pickup_at_cancel.toFixed(1)}h`
                  : "—"}
              </p>
            </div>
          </div>
          {booking.hours_before_pickup_at_cancel != null && (
            <p className="text-slate-300 text-xs mt-3 pt-3 border-t border-slate-500/20">
              {booking.hours_before_pickup_at_cancel < 0
                ? "⚠️ Pickup time had already passed — treat as no-show (full fare per Terms 4.3)."
                : booking.hours_before_pickup_at_cancel >= 4
                ? "✅ Cancelled >4h before pickup — eligible for FREE cancellation (Terms 4.1)."
                : `⚠️ Only ${booking.hours_before_pickup_at_cancel.toFixed(1)}h before pickup — up to 50% fee allowed (Terms 4.2).`}
            </p>
          )}
        </div>
      )}

      {/* Clash Detection */}
      {clashLoading && (
        <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4 flex items-center gap-3">
          <Loader2 className="w-5 h-5 animate-spin text-sarathi-text-muted" />
          <span className="text-sarathi-text-muted text-sm">Checking schedule availability...</span>
        </div>
      )}
      {clashData && !clashLoading && (
        <div className={`rounded-xl p-4 border ${clashStyle(clashData.status).bg}`} data-testid="clash-detection">
          {/* Header */}
          <div className="flex items-center gap-2 mb-2">
            {clashData.status === "SAFE" ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : (
              <ShieldAlert className={`w-5 h-5 ${clashStyle(clashData.status).text}`} />
            )}
            <span className={`font-semibold text-sm ${clashStyle(clashData.status).text}`}>
              {clashStyle(clashData.status).label}
            </span>
          </div>

          {/* Vehicle Info */}
          {(clashData.vehicleFreeAtDisplay || clashData.earliestFeasiblePickupDisplay) && (
            <div className="flex gap-4 mb-3 text-xs">
              {clashData.vehicleFreeAtDisplay && (
                <div className="bg-white/5 rounded-lg px-3 py-2">
                  <span className="text-sarathi-text-muted block">Vehicle free at</span>
                  <span className="text-white font-semibold">{clashData.vehicleFreeAtDisplay}</span>
                </div>
              )}
              {clashData.earliestFeasiblePickupDisplay && (
                <div className="bg-white/5 rounded-lg px-3 py-2">
                  <span className="text-sarathi-text-muted block">Earliest feasible pickup</span>
                  <span className="text-sarathi-primary font-semibold">{clashData.earliestFeasiblePickupDisplay}</span>
                </div>
              )}
            </div>
          )}

          {/* Alerts */}
          {clashData.alerts && clashData.alerts.length > 0 ? (
            <div className="space-y-2">
              {clashData.alerts.map((alert, i) => (
                <div key={alert.title || `alert-${i}`} className={`rounded-lg p-3 border ${
                  alert.severity === "red" ? "bg-red-500/5 border-red-500/20" : "bg-yellow-500/5 border-yellow-500/20"
                }`}>
                  <p className={`text-sm font-medium ${alert.severity === "red" ? "text-red-400" : "text-yellow-400"}`}>
                    {alert.severity === "red" ? "🔴" : "🟡"} {alert.title}
                  </p>
                  <p className="text-sarathi-text-secondary text-xs mt-1 leading-relaxed">{alert.message}</p>
                  {alert.conflictingBooking && (
                    <p className="text-sarathi-text-muted text-xs mt-1">
                      Conflicting ride: {alert.conflictingBooking} · {alert.conflictingRoute}
                    </p>
                  )}
                  {alert.bufferBreakdown && (
                    <p className="text-sarathi-text-muted text-xs mt-1 font-mono">{alert.bufferBreakdown}</p>
                  )}
                </div>
              ))}
            </div>
          ) : clashData.status !== "SAFE" && (
            <p className="text-sarathi-text-secondary text-xs">A potential scheduling conflict was detected with a nearby confirmed booking. Check the calendar for details.</p>
          )}
        </div>
      )}

      {/* Customer Details */}
      <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4">
        <h2 className="text-white font-semibold text-sm mb-3">Customer</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div><span className="text-sarathi-text-muted text-xs block">Name</span><span className="text-white">{booking.passenger_name}</span></div>
          <div><span className="text-sarathi-text-muted text-xs block">Phone</span><span className="text-white">{booking.passenger_phone || <span className="text-sarathi-text-muted italic">—</span>}</span></div>
          {booking.passenger_email && <div><span className="text-sarathi-text-muted text-xs block">Email</span><span className="text-white">{booking.passenger_email}</span></div>}
          <div><span className="text-sarathi-text-muted text-xs block">Passengers</span><span className="text-white">{booking.passengers}</span></div>
        </div>
        {booking.passenger_phone && (
          <div className="flex gap-2 mt-3">
            <a href={`tel:${booking.passenger_phone}`} className="inline-flex items-center gap-1.5 text-xs bg-sarathi-background border border-white/10 rounded-lg px-3 py-1.5 text-sarathi-text-secondary hover:text-white">
              <Phone className="w-3 h-3" /> Call
            </a>
            <a href={buildWhatsAppConfirmUrl(booking)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs bg-green-600/15 border border-green-600/30 rounded-lg px-3 py-1.5 text-green-400 hover:text-green-300">
              <MessageSquare className="w-3 h-3" /> WhatsApp
            </a>
          </div>
        )}
      </div>

      {/* Copy Message Buttons */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => handleCopyMsg("confirm")}
          className="flex-1 flex items-center justify-center gap-2 bg-green-600/10 border border-green-600/25 rounded-xl py-3 text-sm font-medium text-green-400 hover:bg-green-600/15 transition-colors" data-testid="copy-confirm-msg">
          {copiedBtn === "confirm" ? <><ClipboardCheck className="w-4 h-4" /> Copied ✓</> : <><Copy className="w-4 h-4" /> Copy Confirmation Message</>}
        </button>
        <button
          type="button"
          onClick={() => handleCopyMsg("decline")}
          className="flex-1 flex items-center justify-center gap-2 bg-red-500/10 border border-red-500/25 rounded-xl py-3 text-sm font-medium text-red-400 hover:bg-red-500/15 transition-colors" data-testid="copy-decline-msg">
          {copiedBtn === "decline" ? <><ClipboardCheck className="w-4 h-4" /> Copied ✓</> : <><Copy className="w-4 h-4" /> Copy Decline Message</>}
        </button>
      </div>

      {/* Trip Details */}
      <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4">
        <h2 className="text-white font-semibold text-sm mb-3">Trip Details</h2>
        <div className="space-y-2.5 text-sm">
          <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-sarathi-primary mt-0.5 shrink-0" /><div><span className="text-sarathi-text-muted text-xs block">Pickup</span><span className="text-white">{booking.pickup_location}</span></div></div>
          <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-red-400 mt-0.5 shrink-0" /><div><span className="text-sarathi-text-muted text-xs block">Drop</span><span className="text-white">{booking.drop_location}</span></div></div>
          <div className="grid grid-cols-3 gap-3">
            <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-sarathi-text-muted" /><div><span className="text-sarathi-text-muted text-xs block">Date & Time</span><span className="text-white text-xs">{booking.date} · {booking.time}</span></div></div>
            <div className="flex items-center gap-2"><Users className="w-4 h-4 text-sarathi-text-muted" /><div><span className="text-sarathi-text-muted text-xs block">Type</span><span className="text-white text-xs">{booking.ride_type}</span></div></div>
            {booking.flight_number && <div className="flex items-center gap-2"><Plane className="w-4 h-4 text-sarathi-text-muted" /><div><span className="text-sarathi-text-muted text-xs block">Flight</span><span className="text-white text-xs">{booking.flight_number}</span></div></div>}
          </div>
          {booking.special_requests && <div><span className="text-sarathi-text-muted text-xs block">Special Requests</span><span className="text-white text-xs">{booking.special_requests}</span></div>}
        </div>
      </div>

      {/* WhatsApp Status */}
      <WhatsAppStatusPanel bookingId={id} bookingStatus={booking.status} />

      {/* Reschedule pickup time (admin-only). Blocked for terminal statuses. */}
      <RescheduleSection booking={booking} setBooking={setBooking} />

      {/* T-60 ride reminder status + manual override */}
      <ReminderControl booking={booking} setBooking={setBooking} />

      {/* Fare Breakdown */}
      {fare && fare.baseFare != null && (
        <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4">
          <h2 className="text-white font-semibold text-sm mb-3">Fare Breakdown</h2>
          <div className="space-y-1.5">
            {fare.invoiceLines ? fare.invoiceLines.map((line, i) => (
              <div key={line.description || `line-${i}`} className={`flex justify-between text-sm ${line.isFinal ? "border-t border-white/10 pt-2 mt-2" : ""}`}>
                <span className={`${line.isBold ? "text-white font-semibold" : "text-sarathi-text-secondary"}`}>
                  {line.description}
                  {line.detail && <span className="text-sarathi-text-muted text-xs ml-1">({line.detail})</span>}
                </span>
                <span className={`${line.isBold ? "text-sarathi-primary font-bold" : "text-white"}`}>₹{line.amount?.toLocaleString("en-IN")}</span>
              </div>
            )) : (
              <>
                {/* Trip info */}
                {(fare.tripType || fare.zone || fare.ratePerKm) && (
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-sarathi-text-muted text-xs">
                      {fare.tripType}{fare.zone ? ` · ${fare.zone}` : ""}{fare.ratePerKm ? ` · ₹${fare.ratePerKm}/km` : ""}
                    </span>
                  </div>
                )}
                {/* Base fare */}
                <div className="flex justify-between text-sm"><span className="text-sarathi-text-secondary">Base fare</span><span className="text-white">₹{fare.baseFare?.toLocaleString("en-IN")}</span></div>
                {/* Night surcharge — manual bookings emit an object, legacy emits a scalar; both handled here */}
                {(() => {
                  const surchargeAmt = typeof fare.nightSurcharge === "number"
                    ? fare.nightSurcharge
                    : (fare.nightSurcharge?.amount || 0);
                  return surchargeAmt > 0 ? (
                    <div className="flex justify-between text-sm"><span className="text-sarathi-text-secondary">Night surcharge</span><span className="text-white">₹{surchargeAmt.toLocaleString("en-IN")}</span></div>
                  ) : null;
                })()}
                {/* Mandatory tolls */}
                {fare.mandatoryTolls?.map((t, i) => (
                  <div key={`mt-${i}`} className="flex justify-between text-sm"><span className="text-sarathi-text-secondary">{t.id === "sadahalli" ? "Airport Road Toll (Sadahalli)" : t.name}</span><span className="text-white">₹{t.amount?.toLocaleString("en-IN")}</span></div>
                ))}
                {/* Toll breakdown fallback (old bookings) */}
                {!fare.mandatoryTolls?.length && fare.tollBreakdown?.map((t, i) => (
                  <div key={`tb-${i}`} className="flex justify-between text-sm"><span className="text-sarathi-text-secondary">{t.name}</span><span className="text-white">₹{t.amount?.toLocaleString("en-IN")}</span></div>
                ))}
                {/* Additive-mode individual toll lines (manual bookings + additive pricing engine output) */}
                {!fare.mandatoryTolls?.length && !fare.tollBreakdown?.length && (
                  <>
                    {fare.tollSadahalli > 0 && (
                      <div className="flex justify-between text-sm"><span className="text-sarathi-text-secondary">Devanahalli / Sadahalli Toll</span><span className="text-white">₹{fare.tollSadahalli.toLocaleString("en-IN")}</span></div>
                    )}
                    {fare.tollMysurExp > 0 && (
                      <div className="flex justify-between text-sm"><span className="text-sarathi-text-secondary">Bengaluru–Mysuru Expressway Toll</span><span className="text-white">₹{fare.tollMysurExp.toLocaleString("en-IN")}</span></div>
                    )}
                    {fare.tollNiceRoad > 0 && (
                      <div className="flex justify-between text-sm"><span className="text-sarathi-text-secondary">NICE Road Toll</span><span className="text-white">₹{fare.tollNiceRoad.toLocaleString("en-IN")}</span></div>
                    )}
                  </>
                )}
                {/* Toll total fallback */}
                {!fare.mandatoryTolls?.length && !fare.tollBreakdown?.length && !(fare.tollSadahalli > 0 || fare.tollMysurExp > 0 || fare.tollNiceRoad > 0) && fare.totalToll > 0 && (
                  <div className="flex justify-between text-sm"><span className="text-sarathi-text-secondary">Tolls</span><span className="text-white">₹{fare.totalToll?.toLocaleString("en-IN")}</span></div>
                )}
                {/* Driver allowance */}
                {fare.driverAllowance > 0 && <div className="flex justify-between text-sm"><span className="text-sarathi-text-secondary">{fare.driverAllowanceLabel || "Driver allowance"}</span><span className="text-white">₹{fare.driverAllowance?.toLocaleString("en-IN")}</span></div>}
                {/* Surcharges */}
                {fare.surcharges?.map((s, i) => (
                  <div key={`s-${i}`} className="flex justify-between text-sm"><span className="text-sarathi-text-secondary">{s.label || s.name}</span><span className="text-white">₹{s.amount?.toLocaleString("en-IN")}</span></div>
                ))}
                {/* GST */}
                {(fare.cgst > 0 || fare.sgst > 0) && (
                  <div className="border-t border-white/5 pt-2 mt-2">
                    <div className="flex justify-between text-sm"><span className="text-sarathi-text-secondary">CGST @ 2.5%</span><span className="text-white">₹{fare.cgst?.toLocaleString("en-IN")}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-sarathi-text-secondary">SGST @ 2.5%</span><span className="text-white">₹{fare.sgst?.toLocaleString("en-IN")}</span></div>
                  </div>
                )}
                {/* Total */}
                <div className="flex justify-between text-sm border-t border-white/10 pt-2 mt-2"><span className="text-white font-semibold">Total</span><span className="text-sarathi-primary font-bold">{(() => {
                  const t = typeof fare.total === "number" ? fare.total : (typeof fare.displayTotal === "number" ? fare.displayTotal : (typeof fare.totalFare === "number" ? fare.totalFare : null));
                  if (t != null) return `₹${t.toLocaleString("en-IN")}`;
                  // Legacy: displayTotal was sometimes a pre-formatted string
                  return fare.displayTotal || "—";
                })()}</span></div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Payment */}
      <PaymentSection booking={booking} onChanged={refreshBooking} />

      {/* Tax Invoice — admin can download on the customer's behalf */}
      <InvoiceSection booking={booking} />

      {/* Status Change */}
      <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4">
        <h2 className="text-white font-semibold text-sm mb-3">Update Status</h2>
        <div className="flex items-center gap-2">
          <Select value={booking.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="bg-sarathi-background border-white/10 text-white rounded-lg h-9 text-sm w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-sarathi-surface border-white/10">
              {["PENDING", "CONFIRMED", "IN_PROGRESS", "COMPLETED", "PAID", "CANCELLED", "DECLINED"].map(s => (
                <SelectItem key={s} value={s} className="text-white hover:bg-sarathi-elevated text-sm">{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {saving === "status" && <Loader2 className="w-4 h-4 animate-spin text-sarathi-primary" />}
        </div>
      </div>

      {/* Driver & Vehicle Assignment (inline form, shown only for pending bookings) */}
      {isPending && <AssignmentForm booking={booking} setBooking={setBooking} />}

      {/* Action Buttons */}
      {isPending && (
        <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4">
          <div className="flex gap-3">
            <Button onClick={handleConfirm} disabled={saving === "confirm"} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-11" data-testid="confirm-btn">
              {saving === "confirm" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-2" /> Confirm & Send WhatsApp</>}
            </Button>
            <Button onClick={handleDecline} disabled={saving === "decline"} variant="outline" className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10 h-11" data-testid="decline-btn">
              {saving === "decline" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><X className="w-4 h-4 mr-2" /> Decline & Send WhatsApp</>}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}


/* ── Payment Section ──────────────────────────────────────────
   Two actions: send Razorpay payment link OR record offline (UPI/cash) payment.
   Razorpay link button is disabled if Razorpay isn't configured. */
function PaymentSection({ booking, onChanged }) {
  const [busy, setBusy] = useState("");
  const [amount, setAmount] = useState(() => {
    // Use the FINAL all-inclusive fare (with night surcharge, tolls, GST etc.) — not
    // the base `calculatedFare` which excludes surcharges. Bug fix 2026-05-12:
    // previously we used `calculatedFare` and undercharged customers ₹150 on every
    // night-surcharge ride (e.g. customer quoted ₹1,370 → admin saw ₹1,220 → Razorpay
    // link created for ₹1,220).
    const fare = booking?.fare_data?.totalFare
      ?? booking?.fare_data?.calculatedFare
      ?? booking?.fare_data?.total
      ?? 0;
    return fare ? String(fare) : "";
  });
  const [method, setMethod] = useState("upi_offline");
  const [notes, setNotes] = useState("");
  const [rzpAvailable, setRzpAvailable] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/payments/status`)
      .then(r => setRzpAvailable(!!r.data?.razorpay_configured))
      .catch(() => setRzpAvailable(false));
  }, []);

  const status = (booking?.payment_status || "unpaid").toLowerCase();
  const linkUrl = booking?.payment_link_url;
  const paid = status === "paid";

  const sendLink = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount."); return; }
    setBusy("link");
    try {
      const res = await axios.post(`${API}/admin/bookings/${booking.id}/send-payment-link`, {
        amount: amt, notify_sms: true, notify_email: true,
      });
      toast.success("Payment link sent.");
      if (res.data?.payment?.short_url) window.open(res.data.payment.short_url, "_blank");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send payment link.");
    } finally { setBusy(""); }
  };

  const markOffline = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount."); return; }
    setBusy("offline");
    try {
      await axios.post(`${API}/admin/bookings/${booking.id}/mark-paid-offline`, {
        amount: amt, method, notes,
      });
      toast.success("Payment recorded.");
      setNotes("");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't record payment.");
    } finally { setBusy(""); }
  };

  return (
    <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4" data-testid="payment-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-white font-semibold text-sm">Payment</h2>
        <span className={`text-[11px] font-semibold border rounded-md px-2 py-0.5 ${paid ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-amber-500/15 text-amber-400 border-amber-500/30"}`}>
          {paid ? `Paid · ${booking?.payment_source || ""}` : status === "link_sent" ? "Link Sent" : "Unpaid"}
        </span>
      </div>

      {linkUrl && !paid && (
        <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-xs underline mb-3 inline-block">
          View existing link →
        </a>
      )}

      {!paid && (
        <>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Amount (₹)"
              className="bg-sarathi-background border-white/10 text-white rounded-lg h-9 text-sm" data-testid="payment-amount" />
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="bg-sarathi-background border-white/10 text-white rounded-lg h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-sarathi-surface border-white/10">
                <SelectItem value="upi_offline" className="text-white text-sm">UPI (offline)</SelectItem>
                <SelectItem value="cash" className="text-white text-sm">Cash</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Notes (optional)"
            className="bg-sarathi-background border-white/10 text-white rounded-lg h-9 text-sm mb-3" />
          <div className="flex flex-wrap gap-2">
            <Button onClick={markOffline} disabled={!!busy} size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 text-xs" data-testid="mark-paid-offline-btn">
              {busy === "offline" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Mark Paid (UPI / Cash)"}
            </Button>
            <Button onClick={sendLink} disabled={!!busy || !rzpAvailable} size="sm"
              className="bg-blue-600 hover:bg-blue-700 text-white h-9 text-xs disabled:opacity-50" data-testid="send-payment-link-btn"
              title={rzpAvailable ? "Send Razorpay payment link via SMS + Email" : "Razorpay not configured"}>
              {busy === "link" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Send Razorpay Link"}
            </Button>
          </div>
          {!rzpAvailable && (
            <p className="text-amber-400/80 text-[11px] mt-2">Razorpay isn't configured. Only offline reconciliation is available.</p>
          )}
        </>
      )}
    </div>
  );
}




/* ── WhatsApp Status Panel ──────────────────────────────────
   Shows automated WhatsApp delivery status (sent/delivered/read/failed) for
   confirm + decline messages sent via MSG91, plus a resend button on failure.
   Polls every 8s while a message is in 'sent' state (waiting for delivery
   receipt webhook). */
function WhatsAppStatusPanel({ bookingId, bookingStatus }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/bookings/${bookingId}/whatsapp`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  // Poll while any message is in 'sent' state (waiting for delivered/read webhook)
  useEffect(() => {
    const msgs = data?.messages || {};
    const anyPending = ["confirm", "decline"].some(
      (k) => msgs[k]?.delivery_status === "sent",
    );
    if (!anyPending) return;
    const t = setInterval(fetchStatus, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/whatsapp/resend`);
      toast.success("WhatsApp resend triggered");
      await fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    } finally {
      setResending(false);
    }
  };

  if (loading) return null;
  if (!data) return null;

  // Not configured → don't render the panel (keeps existing manual flow clean)
  if (!data.configured) return null;

  const msgs = data.messages || {};
  const relevant = bookingStatus === "DECLINED" ? msgs.decline : msgs.confirm;
  if (!relevant) return null; // no WhatsApp activity yet for this booking

  const statusInfo = (s) => {
    switch (s) {
      case "read":      return { label: "Read",      cls: "bg-blue-500/15 text-blue-400 border-blue-500/30",     pulse: false };
      case "delivered": return { label: "Delivered", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", pulse: false };
      case "sent":      return { label: "Sent",      cls: "bg-amber-500/15 text-amber-400 border-amber-500/30",   pulse: true };
      case "failed":    return { label: "Failed",    cls: "bg-red-500/15 text-red-400 border-red-500/30",         pulse: false };
      default:          return { label: s || "—",    cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",      pulse: false };
    }
  };
  const s = statusInfo(relevant.delivery_status);

  return (
    <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4" data-testid="whatsapp-status">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-green-400" />
          <h2 className="text-white font-semibold text-sm">WhatsApp ({bookingStatus === "DECLINED" ? "Decline" : "Confirmation"})</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] font-semibold border ${s.cls}`}>
            {s.pulse && <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 mr-1 animate-pulse"></span>}
            {s.label}
          </span>
          <button
            type="button"
            onClick={fetchStatus}
            className="text-sarathi-text-muted hover:text-white transition-colors"
            title="Refresh"
            data-testid="whatsapp-refresh">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      <p className="text-sarathi-text-muted text-xs">
        Sent {new Date(relevant.created_at).toLocaleString("en-IN")}
        {relevant.status_updated_at && relevant.status_updated_at !== relevant.created_at && (
          <> · Updated {new Date(relevant.status_updated_at).toLocaleString("en-IN")}</>
        )}
      </p>
      {relevant.delivery_status === "failed" && (
        <div className="mt-3 bg-red-500/5 border border-red-500/20 rounded-lg p-3">
          <p className="text-red-400 text-xs mb-2">
            Delivery failed: <span className="text-red-300/80">{relevant.error || "Unknown reason"}</span>
          </p>
          <p className="text-sarathi-text-muted text-[11px] mb-2">
            Fall back to the "Copy {bookingStatus === "DECLINED" ? "Decline" : "Confirmation"} Message" button above, or retry below.
          </p>
          <Button
            onClick={handleResend}
            disabled={resending}
            size="sm"
            className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white"
            data-testid="whatsapp-resend">
            {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Send className="w-3 h-3 mr-1" /> Retry Send</>}
          </Button>
        </div>
      )}
    </div>
  );
}



/* ── Driver & Vehicle Inline Assignment Form ────────────────────────────
   Auto-saves each field on blur (only if changed). Tiny green tick appears
   for ~2s on successful save. All 4 fields pre-populated from booking
   (which itself has env defaults applied at creation time). */
function AssignmentForm({ booking, setBooking }) {
  const fields = [
    { key: "driver_name",          label: "Driver Name",         icon: User,    placeholder: "Full name",        testid: "driver-name" },
    { key: "driver_phone",         label: "Driver Phone",        icon: Phone,   placeholder: "10-digit mobile",  testid: "driver-phone",        prefix: "+91", numeric: true, max: 10 },
    { key: "vehicle_name",         label: "Vehicle",             icon: Car,     placeholder: "MG Windsor EV",    testid: "vehicle-name" },
    { key: "vehicle_registration", label: "Registration No.",    icon: IdCard,  placeholder: "KA01AT8260",       testid: "vehicle-reg",         upper: true },
  ];

  const [values, setValues] = useState({
    driver_name: booking.driver_name || "",
    driver_phone: booking.driver_phone || "",
    vehicle_name: booking.vehicle_name || "",
    vehicle_registration: booking.vehicle_registration || "",
  });
  const [saved, setSaved] = useState({});  // { field: timestamp } for the green tick
  const [saving, setSaving] = useState({}); // { field: bool }

  const handleChange = (key, raw, opts) => {
    let v = raw;
    if (opts?.numeric) v = v.replace(/\D/g, "").slice(0, opts.max || 10);
    if (opts?.upper) v = v.toUpperCase();
    setValues((s) => ({ ...s, [key]: v }));
  };

  const handleBlur = async (key) => {
    const current = values[key];
    const original = booking[key] || "";
    if (current === original) return; // no change → no save
    setSaving((s) => ({ ...s, [key]: true }));
    try {
      const res = await axios.put(`${API}/admin/bookings/${booking.booking_reference}/assignment`, { [key]: current });
      setBooking(res.data);
      setSaved((s) => ({ ...s, [key]: Date.now() }));
      setTimeout(() => setSaved((s) => { const c = { ...s }; delete c[key]; return c; }), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save");
      // Revert on failure
      setValues((s) => ({ ...s, [key]: original }));
    } finally {
      setSaving((s) => ({ ...s, [key]: false }));
    }
  };

  return (
    <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4" data-testid="assignment-form">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-white font-semibold text-sm">Driver & Vehicle</h2>
        <p className="text-sarathi-text-muted text-[11px]">Sent in WhatsApp confirmation · auto-saves on tab-out</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {fields.map((f) => (
          <div key={f.key} className="relative">
            <label className="text-sarathi-text-secondary text-[11px] uppercase tracking-wider font-semibold flex items-center gap-1.5 mb-1.5">
              <f.icon className="w-3 h-3" /> {f.label}
            </label>
            <div className="relative">
              {f.prefix && (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sarathi-text-muted text-sm pointer-events-none">{f.prefix}</span>
              )}
              <Input
                value={values[f.key]}
                onChange={(e) => handleChange(f.key, e.target.value, { numeric: f.numeric, max: f.max, upper: f.upper })}
                onBlur={() => handleBlur(f.key)}
                placeholder={f.placeholder}
                inputMode={f.numeric ? "numeric" : undefined}
                className={`bg-sarathi-background border-white/10 text-white rounded-lg h-9 text-sm ${f.prefix ? "pl-10" : ""} pr-8`}
                data-testid={`assignment-${f.testid}-input`}
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2">
                {saving[f.key] && <Loader2 className="w-3.5 h-3.5 text-sarathi-text-muted animate-spin" />}
                {!saving[f.key] && saved[f.key] && <Check className="w-3.5 h-3.5 text-emerald-400" />}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}



/* ── Reschedule Section ─────────────────────────────────────────────────
   Admin-only widget to change pickup date/time on an existing booking.
   Hidden for terminal/in-flight statuses (CANCELLED / DECLINED / COMPLETED
   / PAID / IN_PROGRESS) — those bookings cannot be rescheduled, only
   refunded or re-created.

   Format contract: backend stores `date` as "%B %d, %Y" (e.g. "June 18,
   2026") and `time` as "%I:%M %p" (e.g. "5:00 PM"). HTML date/time inputs
   use ISO formats, so we convert in both directions.

   On HARD_CLASH (409 from backend) we surface a warning banner with the
   clash details and a "Reschedule anyway" force button. */
const RESCHEDULE_BLOCKED = ["CANCELLED", "DECLINED", "COMPLETED", "PAID", "IN_PROGRESS"];

function toCanonicalDate(htmlDate) {
  // "2026-06-21" → "June 21, 2026"
  if (!htmlDate) return "";
  const [y, m, d] = htmlDate.split("-").map(Number);
  if (!y || !m || !d) return "";
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function toCanonicalTime(htmlTime) {
  // "17:30" → "5:30 PM"
  if (!htmlTime) return "";
  const [hh, mm] = htmlTime.split(":").map(Number);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return "";
  const dt = new Date(2000, 0, 1, hh, mm);
  return dt.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

function fromCanonicalDate(canonical) {
  // "June 21, 2026" → "2026-06-21"
  if (!canonical) return "";
  const cleaned = canonical.replace(/(\d+)(st|nd|rd|th)/, "$1");
  const dt = new Date(cleaned);
  if (Number.isNaN(dt.getTime())) return "";
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function fromCanonicalTime(canonical) {
  // "5:30 PM" or "17:30" → "17:30"
  if (!canonical) return "";
  const ampm = canonical.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = ampm[2];
    const meridian = ampm[3].toUpperCase();
    if (meridian === "PM" && h !== 12) h += 12;
    if (meridian === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${m}`;
  }
  const h24 = canonical.match(/^(\d{1,2}):(\d{2})$/);
  if (h24) return `${String(parseInt(h24[1], 10)).padStart(2, "0")}:${h24[2]}`;
  return "";
}

function RescheduleSection({ booking, setBooking }) {
  const status = (booking?.status || "").toUpperCase();
  const blocked = RESCHEDULE_BLOCKED.includes(status);

  const [open, setOpen] = useState(false);
  const [newDate, setNewDate] = useState(() => fromCanonicalDate(booking.date));
  const [newTime, setNewTime] = useState(() => fromCanonicalTime(booking.time));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [clashWarn, setClashWarn] = useState(null);
  const history = booking.reschedule_history || [];

  // Re-prime inputs when the underlying booking changes (e.g. after save).
  useEffect(() => {
    setNewDate(fromCanonicalDate(booking.date));
    setNewTime(fromCanonicalTime(booking.time));
  }, [booking.date, booking.time]);

  const submit = async (force = false) => {
    if (!newDate || !newTime) {
      toast.error("Please pick both a date and a time.");
      return;
    }
    const canonicalDate = toCanonicalDate(newDate);
    const canonicalTime = toCanonicalTime(newTime);
    if (!canonicalDate || !canonicalTime) {
      toast.error("Couldn't parse the date/time you entered.");
      return;
    }
    if (canonicalDate === booking.date && canonicalTime === booking.time) {
      toast.error("New slot is identical to the current pickup.");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.put(
        `${API}/admin/bookings/${booking.booking_reference || booking.id}/reschedule`,
        { date: canonicalDate, time: canonicalTime, reason: reason.trim() || null, force },
      );
      setBooking(res.data);
      setClashWarn(null);
      setReason("");
      setOpen(false);
      const wa = res.data._whatsapp_send || {};
      if (wa.success && !wa.skipped) {
        toast.success("Rescheduled · WhatsApp sent to customer");
      } else if (wa.skipped) {
        toast.success("Rescheduled (WhatsApp send was a duplicate · already delivered)");
      } else if (wa.error_code === "CONFIG") {
        toast.success("Rescheduled — please WhatsApp the customer manually.");
      } else {
        toast.warning("Rescheduled, but WhatsApp send failed. Please notify the customer.");
      }
    } catch (e) {
      if (e?.response?.status === 409) {
        // Hard clash — surface the conflict and let admin force.
        const detail = e.response.data?.detail;
        setClashWarn(detail || { message: "Schedule conflict." });
        toast.error("Slot conflicts with another confirmed ride.");
      } else if (e?.response?.status !== 401) {
        toast.error(e?.response?.data?.detail || "Couldn't reschedule.");
      }
    } finally {
      setBusy(false);
    }
  };

  if (blocked) return null;

  return (
    <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4" data-testid="reschedule-section">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <CalendarClock className="w-4 h-4 text-sarathi-primary shrink-0" />
          <h2 className="text-white font-semibold text-sm">Reschedule pickup</h2>
        </div>
        {!open && (
          <Button
            onClick={() => { setOpen(true); setClashWarn(null); }}
            size="sm"
            variant="outline"
            className="h-8 text-xs border-sarathi-primary/40 text-sarathi-primary hover:bg-sarathi-primary/10"
            data-testid="reschedule-open-btn"
          >
            Change date/time
          </Button>
        )}
      </div>

      <div className="text-xs text-sarathi-text-muted">
        Current pickup:{" "}
        <span className="text-white">{booking.date} · {booking.time}</span>
      </div>

      {open && (
        <div className="mt-4 space-y-3" data-testid="reschedule-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sarathi-text-secondary text-[11px] uppercase tracking-wider font-semibold block mb-1.5">New Date</label>
              <Input
                type="date"
                value={newDate}
                onChange={(e) => { setNewDate(e.target.value); setClashWarn(null); }}
                min={new Date().toISOString().slice(0, 10)}
                className="bg-sarathi-background border-white/10 text-white rounded-lg h-9 text-sm"
                data-testid="reschedule-date-input"
              />
            </div>
            <div>
              <label className="text-sarathi-text-secondary text-[11px] uppercase tracking-wider font-semibold block mb-1.5">New Time</label>
              <Input
                type="time"
                value={newTime}
                onChange={(e) => { setNewTime(e.target.value); setClashWarn(null); }}
                className="bg-sarathi-background border-white/10 text-white rounded-lg h-9 text-sm"
                data-testid="reschedule-time-input"
              />
            </div>
          </div>

          <div>
            <label className="text-sarathi-text-secondary text-[11px] uppercase tracking-wider font-semibold block mb-1.5">Reason (optional, internal note)</label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Customer flight delay, requested different slot, etc."
              className="bg-sarathi-background border-white/10 text-white rounded-lg h-9 text-sm"
              data-testid="reschedule-reason-input"
            />
          </div>

          {newDate && newTime && (
            <p className="text-[11px] text-sarathi-text-muted">
              New slot will be sent to customer as:{" "}
              <span className="text-white">{toCanonicalDate(newDate)} · {toCanonicalTime(newTime)}</span>
            </p>
          )}

          {clashWarn && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="reschedule-clash-warning">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-red-400 text-xs font-semibold mb-1">Hard schedule conflict</p>
                  <p className="text-red-300/80 text-xs">
                    {clashWarn?.clash?.message || clashWarn?.message || "Another confirmed ride overlaps with this slot."}
                  </p>
                  {clashWarn?.clash?.alerts?.length > 0 && (
                    <ul className="mt-1.5 list-disc list-inside text-red-300/70 text-xs space-y-0.5">
                      {clashWarn.clash.alerts.slice(0, 3).map((a, i) => (
                        <li key={i}>{a.message || JSON.stringify(a)}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              onClick={() => submit(false)}
              disabled={busy}
              className="bg-sarathi-primary hover:bg-sarathi-primary-hover text-white h-9 text-sm"
              data-testid="reschedule-submit-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CalendarClock className="w-4 h-4 mr-2" /> Reschedule & notify</>}
            </Button>
            {clashWarn && (
              <Button
                onClick={() => submit(true)}
                disabled={busy}
                variant="outline"
                className="border-red-500/40 text-red-400 hover:bg-red-500/10 h-9 text-sm"
                data-testid="reschedule-force-btn"
              >
                Reschedule anyway
              </Button>
            )}
            <Button
              onClick={() => { setOpen(false); setClashWarn(null); setReason(""); }}
              disabled={busy}
              variant="ghost"
              className="text-sarathi-text-muted hover:text-white h-9 text-sm"
              data-testid="reschedule-cancel-btn"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-4 pt-3 border-t border-white/5" data-testid="reschedule-history">
          <div className="flex items-center gap-1.5 mb-2">
            <History className="w-3 h-3 text-sarathi-text-muted" />
            <p className="text-sarathi-text-muted text-[11px] uppercase tracking-wider font-semibold">Reschedule log</p>
          </div>
          <ul className="space-y-1.5">
            {history.slice().reverse().map((h, i) => (
              <li key={h.at || i} className="text-[11px] text-sarathi-text-secondary">
                <span className="text-white">{h.old_date} · {h.old_time}</span>
                <span className="text-sarathi-text-muted"> → </span>
                <span className="text-white">{h.new_date} · {h.new_time}</span>
                <span className="text-sarathi-text-muted"> by {h.by} · {new Date(h.at).toLocaleString("en-IN")}</span>
                {h.forced && <span className="ml-1 text-red-400">(forced)</span>}
                {h.reason && <div className="text-sarathi-text-muted italic ml-2">&ldquo;{h.reason}&rdquo;</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


/* ── ReminderControl ────────────────────────────────────────────────────
   Surfaces the T-60 ride-reminder state and exposes a manual "Send now"
   button for edge cases the scheduler missed (deployment restart at the
   exact window, ad-hoc customer request, etc.).

   Hidden for terminal statuses (CANCELLED / DECLINED / COMPLETED / PAID)
   — no point reminding for a ride that's already over or aborted.

   The scheduler fires automatically when pickup is 55–70 mins away; this
   widget just lets the admin see when (or whether) that happened and
   force-resend with a single click if needed. */
const REMINDER_HIDDEN_STATUSES = ["CANCELLED", "DECLINED", "COMPLETED", "PAID"];

function ReminderControl({ booking, setBooking }) {
  const [busy, setBusy] = useState(false);
  const status = (booking?.status || "").toUpperCase();
  if (REMINDER_HIDDEN_STATUSES.includes(status)) return null;

  const sentAt = booking.reminder_60m_sent_at;
  const sentLabel = sentAt ? new Date(sentAt).toLocaleString("en-IN") : null;
  const resultRaw = booking.reminder_60m_result || "";
  const lastError = booking.reminder_60m_last_error;
  const wasSkippedNoMsg91 = resultRaw.includes("msg91_not_configured");

  const send = async (force) => {
    setBusy(true);
    try {
      const ref = booking.booking_reference || booking.id;
      const res = await axios.post(
        `${API}/admin/bookings/${ref}/send-reminder-60m`,
        { force: !!force },
      );
      const ok = res.data?.success;
      if (ok) toast.success("Reminder sent");
      else if ((res.data?.error_code || "").toUpperCase() === "CONFIG")
        toast.warning("MSG91 not configured — reminder skipped (booking marked sent).");
      else toast.error(res.data?.error || "Reminder send failed");
      // Re-fetch the updated booking so the panel reflects the new flags.
      try {
        const fresh = await axios.get(`${API}/admin/bookings/${ref}`);
        setBooking(fresh.data);
      } catch { /* noop */ }
    } catch (e) {
      if (e?.response?.status !== 401) {
        toast.error(e?.response?.data?.detail || "Couldn't send reminder.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4" data-testid="reminder-control">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <BellRing className="w-4 h-4 text-sarathi-primary shrink-0" />
          <h2 className="text-white font-semibold text-sm">1-hour ride reminder</h2>
        </div>
        {!sentAt && (
          <Button
            onClick={() => send(false)}
            disabled={busy}
            size="sm"
            variant="outline"
            className="h-8 text-xs border-sarathi-primary/40 text-sarathi-primary hover:bg-sarathi-primary/10"
            data-testid="reminder-send-btn"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Send now"}
          </Button>
        )}
        {sentAt && (
          <Button
            onClick={() => send(true)}
            disabled={busy}
            size="sm"
            variant="ghost"
            className="h-8 text-xs text-sarathi-text-muted hover:text-white"
            data-testid="reminder-resend-btn"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : "Resend"}
          </Button>
        )}
      </div>

      {sentAt ? (
        <div className="text-xs text-sarathi-text-secondary flex items-start gap-1.5">
          <CheckCircle className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
          <span>
            {wasSkippedNoMsg91
              ? <>Skipped &mdash; MSG91 unconfigured. <span className="text-sarathi-text-muted">Marked at {sentLabel}.</span></>
              : <>Sent <span className="text-white">{sentLabel}</span>{" "}
                <span className="text-sarathi-text-muted">
                  ({resultRaw.startsWith("manual:") ? "manual" : "auto"})
                </span></>}
          </span>
        </div>
      ) : (
        <div className="text-xs text-sarathi-text-muted">
          Will fire automatically about 1 hour before pickup. Use{" "}
          <span className="text-white">Send now</span> if the customer is
          waiting on confirmation right now.
        </div>
      )}

      {lastError && !sentAt && (
        <div className="mt-2 text-[11px] text-red-400">
          Last attempt failed: {lastError}
        </div>
      )}
    </div>
  );
}



/* ── InvoiceSection ─────────────────────────────────────────────────────
   Surfaces the Tax Invoice PDF download for admins. The backend endpoint
   `GET /api/admin/bookings/{id}/invoice.pdf` enforces the same eligibility
   rule as the customer endpoint — status must be COMPLETED or PAID AND
   payment_status must be paid. We mirror that gate in the UI so the
   button only appears when a download will actually succeed; the gate
   text below explains what's blocking it otherwise. */
function InvoiceSection({ booking }) {
  const [busy, setBusy] = useState(false);
  const status = (booking?.status || "").toUpperCase();
  const payStatus = (booking?.payment_status || "").toLowerCase();
  const eligible = ["COMPLETED", "PAID"].includes(status) && ["paid", "completed"].includes(payStatus);

  const download = async () => {
    setBusy(true);
    try {
      const ref = booking.booking_reference || booking.id;
      const res = await axios.get(`${API}/admin/bookings/${ref}/invoice.pdf`, { responseType: "blob" });
      // Pull the filename out of Content-Disposition if the server set one;
      // otherwise fall back to a sensible default with the booking ref.
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : `Sarathi-X_Invoice_${ref}.pdf`;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch (e) {
      if (e?.response?.status === 409) {
        // The backend's blob response means we need to read the JSON
        // error message from the Blob, not e.response.data directly.
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          toast.error(parsed.detail || "Invoice not available yet.");
        } catch {
          toast.error("Invoice not available yet — booking must be completed and paid.");
        }
      } else if (e?.response?.status !== 401) {
        toast.error("Couldn't download invoice.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-sarathi-surface border border-white/10 rounded-xl p-4" data-testid="invoice-section">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-4 h-4 text-sarathi-primary shrink-0" />
          <h2 className="text-white font-semibold text-sm">Tax Invoice</h2>
        </div>
        {eligible && (
          <Button
            onClick={download}
            disabled={busy}
            size="sm"
            variant="outline"
            className="h-8 text-xs border-sarathi-primary/40 text-sarathi-primary hover:bg-sarathi-primary/10"
            data-testid="invoice-download-btn"
          >
            {busy ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <><Download className="w-3 h-3 mr-1.5" /> Download PDF</>
            )}
          </Button>
        )}
      </div>

      {eligible ? (
        <p className="text-xs text-sarathi-text-muted">
          GST tax invoice for this completed ride. Download to share with the
          customer for reimbursement or accounting.
        </p>
      ) : (
        <p className="text-xs text-sarathi-text-muted">
          {status !== "COMPLETED" && status !== "PAID"
            ? "Mark the ride COMPLETED to unlock the invoice."
            : "Record the customer's payment to unlock the invoice."}
        </p>
      )}
    </div>
  );
}

